import { Check, ChevronDown, ChevronRight, MapPinned, RotateCcw, X } from "lucide-react";
import { type ReactNode, useState } from "react";

import type { Direx, DirexIncident } from "../../api/types";
import { IncidentCode } from "../../components/IncidentCode";
import { motion } from "../../components/presence";
import { Badge, Button } from "../../components/ui";
import { t } from "../../i18n/i18n";
import { term } from "../../i18n/vocabulary";
import { plural } from "../../plural";

export type ProposalDecision = "keep" | "dismiss" | "restore";

/** Incidents proposés par les événements posés sur la carte (annotations APP-6, docs/08 §19) : chaque EEI, émeute,
 *  embuscade, manifestation… posé sur la carte propose un incident pré-rempli (position, temps, cellule). L'animateur
 *  le retient (il rejoint la DIREX, « prévu ») ou l'écarte ; rien n'est injecté d'office. « Retenir » du premier
 *  incident proposé clignote : c'est l'action attendue. Les écartés se rétablissent. L'observateur consulte ; une
 *  cellule ne voit jamais ce bandeau (le serveur ne lui envoie aucune proposition). */
export function ProposalsBand({
  data,
  busy,
  language,
  onDecide,
  onKeepAll,
}: {
  data: Direx;
  busy: boolean;
  language: string;
  onDecide: (incident: DirexIncident, decision: ProposalDecision) => void;
  onKeepAll: () => void;
}) {
  const proposals = data.proposals ?? [];
  const dismissed = data.dismissed ?? [];
  const animator = data.me.role === "animateur";
  const [showDismissed, setShowDismissed] = useState(false);
  return (
    <section className="rounded-xl border border-line bg-surface shadow-card" aria-label={t("Incidents proposés par la carte")}>
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5">
        <MapPinned size={16} className="shrink-0 text-accent" />
        <h2 className="text-ui font-semibold text-ink">{t("Incidents proposés par la carte")}</h2>
        {proposals.length > 0 && <Badge tone="accent">{proposals.length}</Badge>}
        {animator && proposals.length > 1 && (
          <Button size="sm" variant="secondary" className="ml-auto" onClick={onKeepAll} disabled={busy}>
            <Check size={14} /> {t("Tout retenir")}
          </Button>
        )}
      </div>
      <p className="px-4 pt-2 text-meta leading-snug text-ink-muted">
        {animator
          ? t(
              "Chaque événement posé sur la carte (EEI, émeute, embuscade, manifestation…) propose un incident. Retenez-le pour l'ajouter à la DIREX, ou écartez-le : rien n'est injecté d'office.",
            )
          : t("Consultation seule : l'animateur retient ou écarte les incidents que proposent les événements posés sur la carte.")}
      </p>
      {proposals.length > 0 ? (
        <ul className="divide-y divide-line px-2 py-1.5">
          {proposals.map((incident, index) => (
            <ProposalRow
              key={incident.id}
              incident={incident}
              language={language}
              actions={
                animator && (
                  <>
                    <Button size="sm" onClick={() => onDecide(incident, "keep")} disabled={busy} highlight={index === 0}>
                      <Check size={14} /> {t("Retenir")}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => onDecide(incident, "dismiss")} disabled={busy}>
                      <X size={14} /> {t("Écarter")}
                    </Button>
                  </>
                )
              }
            />
          ))}
        </ul>
      ) : (
        <p className="px-4 py-2 text-meta leading-snug text-ink-muted">{t("Aucun incident proposé en attente.")}</p>
      )}
      {dismissed.length > 0 && (
        <div className="border-t border-line px-2 py-1.5">
          <Button size="sm" variant="ghost" onClick={() => setShowDismissed((v) => !v)} aria-expanded={showDismissed}>
            {showDismissed ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            {plural(dismissed.length, t("incident écarté"), t("incidents écartés"))}
          </Button>
          {showDismissed && (
            <ul className={`divide-y divide-line ${motion("fade")}`}>
              {dismissed.map((incident) => (
                <ProposalRow
                  key={incident.id}
                  incident={incident}
                  language={language}
                  muted
                  actions={
                    animator && (
                      <Button size="sm" variant="secondary" onClick={() => onDecide(incident, "restore")} disabled={busy}>
                        <RotateCcw size={14} /> {t("Rétablir")}
                      </Button>
                    )
                  }
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function ProposalRow({
  incident,
  language,
  actions,
  muted = false,
}: {
  incident: DirexIncident;
  language: string;
  actions: ReactNode;
  muted?: boolean;
}) {
  const annotation = incident.annotation;
  return (
    <li className={`flex min-h-8 flex-wrap items-center gap-x-3 gap-y-1.5 px-2 py-1.5 ${motion("fade")}`}>
      <span className="flex min-w-0 flex-1 basis-64 flex-col gap-0.5">
        <span className="flex items-center gap-1.5">
          <IncidentCode kind={incident.type} code={incident.code} />
          <span className="font-mono text-meta text-ink-muted tabular-nums">{incident.tempo}</span>
          <span className="text-meta text-ink-muted">· {term(incident.cellule)}</span>
        </span>
        <span lang={language} className={muted ? "text-ui leading-snug text-ink-muted line-through" : "text-ui leading-snug text-ink"}>
          {incident.short}
        </span>
        {annotation && !annotation.orphan && (
          <span className="text-meta leading-snug text-ink-muted">
            {t("Annotation de la carte : {name}", { name: annotation.label ?? "" })}
            {incident.mgrs ? ` · ${incident.mgrs}` : ""}
          </span>
        )}
      </span>
      {actions && <span className="flex shrink-0 gap-1.5">{actions}</span>}
    </li>
  );
}
